import { useState, useCallback, useRef } from "react";
import { useAuthenticatedFetchWithWorkspace } from "./Keycloak/useAuthenticatedFetchWithWorkspace";

type HttpMethod = "POST" | "PATCH" | "PUT" | "DELETE";

interface UseApiMutationOptions<TInput> {
    method?: HttpMethod;
    /** Headers HTTP supplémentaires (ex: "Content-Type": "application/ld+json"). */
    headers?: Record<string, string>;
    /** Construit l'URL dynamiquement depuis le payload (ex: `/resource/${input.id}`). */
    buildEndpoint?: (input: TInput) => string;
    /** Construit le body depuis le payload. Par défaut : JSON.stringify(input). */
    buildBody?: (input: TInput) => BodyInit | undefined;
    /** Callback appelé après un succès. */
    onSuccess?: (input: TInput) => void;
    /** Callback appelé après une erreur. */
    onError?: (error: string, input: TInput) => void;
    /** Message d'erreur fallback (défaut : "Une erreur est survenue"). */
    defaultError?: string;
}

/**
 * Hook générique pour les mutations (POST, PATCH, PUT, DELETE).
 * Retourne `true` en cas de succès, `false` en cas d'erreur.
 *
 * @example
 * // POST simple
 * const { mutate, isLoading, error, success } = useApiMutation<AddPointsPayload>(
 *   '/batch_per_account_misc_moves',
 *   { headers: { "Content-Type": "application/ld+json" } }
 * );
 * await mutate(payload);
 *
 * @example
 * // PATCH avec URL dynamique
 * const { mutate } = useApiMutation<{ id: string; value: boolean }>(
 *   '/subscriptions',
 *   {
 *     method: 'PATCH',
 *     headers: { "Content-Type": "application/merge-patch+json" },
 *     buildEndpoint: ({ id }) => `/subscriptions/${id}`,
 *     buildBody: ({ value }) => JSON.stringify({ isSubscribed: value ? 1 : 0 }),
 *   }
 * );
 */
export function useApiMutation<TInput = void>(endpoint: string, options: UseApiMutationOptions<TInput> = {}) {
    const { method = "POST", defaultError = "Une erreur est survenue" } = options;
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    // Stable ref — évite de recréer `mutate` si les callbacks changent
    const optsRef = useRef(options);
    optsRef.current = options;

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const mutate = useCallback(
        async (input: TInput): Promise<boolean> => {
            if (!isReady) return false;
            const { buildEndpoint, buildBody, headers, onSuccess, onError } = optsRef.current;

            const url = buildEndpoint ? buildEndpoint(input) : endpoint;
            const body =
                buildBody
                    ? buildBody(input)
                    : input !== undefined && input !== null && method !== "DELETE"
                        ? JSON.stringify(input as object)
                        : undefined;

            setIsLoading(true);
            setError("");
            setSuccess(false);
            try {
                await authFetch(url, { method, headers, body });
                setSuccess(true);
                onSuccess?.(input);
                return true;
            } catch (err) {
                const msg = err instanceof Error ? err.message : defaultError;
                setError(msg);
                onError?.(msg, input);
                return false;
            } finally {
                setIsLoading(false);
            }
        },
        [authFetch, isReady, endpoint, method, defaultError]
    );

    const reset = useCallback(() => {
        setError("");
        setSuccess(false);
    }, []);

    return { mutate, isLoading, error, success, reset };
}
